import { fromDateAndTimeInputs } from '@/shared/lib/dates';

/**
 * Onde cada pedaço do estado do calendário mora (spec-calendar.md §8, decisão
 * 55). Dois lugares, e a diferença entre eles é de VIDA ÚTIL:
 *
 * | Estado | Onde | Por quê |
 * |---|---|---|
 * | Filtros | URL | É recorte: colável numa conversa, e voltar desfaz |
 * | Vista e dia selecionado | módulo (aqui) | É posição de leitura de um instante |
 *
 * ⚠️ **A terceira linha desta tabela morreu com o modelo de fontes** (decisão
 * 89, [ADR-015](../../../../docs/adr/ADR-015-agenda-volatil.md)): não há mais
 * "fontes ligadas" para guardar em `localStorage`, porque não há lista de
 * calendários — há a agenda da igreja. A chave `ibft:calendar-sources` deixa de
 * ser lida; ela some sozinha do navegador de quem tinha uma, sem migração e sem
 * consequência (preferência sobre um controle que não existe mais).
 *
 * **Por que dia selecionado não vai para o `localStorage`:** ele é onde a pessoa
 * estava olhando AGORA — persistir isso faria quem abrisse o painel amanhã
 * encontrar **ontem** selecionado, com a lista de um dia que já passou. Memória
 * de módulo é exatamente a vida útil certa: sobrevive a ir e voltar de um
 * projeto dentro da SPA e morre no F5, que recai em hoje.
 */

export type CalendarView = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay';

export const VIEW_LABELS: Record<CalendarView, string> = {
  dayGridMonth: 'Mês',
  timeGridWeek: 'Semana',
  timeGridDay: 'Dia',
};

/** A ordem dos botões é a do macOS: do mais estreito ao mais largo. */
export const VIEW_ORDER: readonly CalendarView[] = [
  'timeGridDay',
  'timeGridWeek',
  'dayGridMonth',
];

// ── Memória de módulo: morre no F5, sobrevive à navegação dentro da SPA ──────

interface ReadingPosition {
  view: CalendarView;
  /** `yyyy-mm-dd` — o dia selecionado; `null` = ainda não escolhido nesta visita. */
  selectedDay: string | null;
  /** `yyyy-mm-dd` — a âncora do período visível, para a grade voltar onde estava. */
  anchor: string | null;
}

const position: ReadingPosition = { view: 'dayGridMonth', selectedDay: null, anchor: null };

export function readPosition(): ReadingPosition {
  return { ...position };
}

export function writePosition(next: Partial<ReadingPosition>): void {
  Object.assign(position, next);
}

// ── O intervalo que a consulta cobre ─────────────────────────────────────────

/**
 * O mês da âncora, **com um mês de folga de cada lado**.
 *
 * Duas razões, e a segunda é a que decide:
 *
 * 1. A vista de mês desenha os dias vizinhos que completam a primeira e a
 *    última semana — agosto começa numa terça e a grade mostra 27 de julho.
 *    Buscar só agosto deixaria aquelas três células vazias mentindo.
 * 2. **A chave de cache tem de ser estável entre as vistas.** Se o intervalo
 *    fosse o período visível, trocar de Mês para Semana dispararia uma consulta
 *    nova e a lista do dia piscaria a cada clique no seletor de vista. Com o
 *    mês da âncora, Dia, Semana e Mês de agosto compartilham UMA entrada.
 *
 * O custo é conhecido e pequeno: RNF-P02 fala em até 200 projetos por mês, e
 * três meses cabem folgados numa resposta (regra 14 — não otimize para a escala
 * que não existe).
 */
export function calendarRange(anchorDay: string): { from: string; to: string } {
  const [year, month] = anchorDay.split('-').map(Number);
  if (!year || !month) throw new Error(`Âncora em formato inesperado: "${anchorDay}"`);

  return {
    from: monthStart(year, month - 1),
    to: monthStart(year, month + 2),
  };
}

/** `2026-08-01T00:00:00-03:00`, com a virada de ano resolvida pelo próprio `Date`. */
function monthStart(year: number, month: number): string {
  const normalized = new Date(Date.UTC(year, month - 1, 1));
  const iso = normalized.toISOString().slice(0, 10);
  return fromDateAndTimeInputs(iso, '00:00');
}
